import PropTypes from 'prop-types';
import { DataTable as PepsicoDsDataTable } from '@pepsico-ds/ui';
import { TableSkeletonLoader } from '@/components/atoms';
import { getCellDataColumns } from '@/components/organisms/DynamicColumnTable/DynamicColumnTableUtils';
import { SIZE } from '@/shared/constants/globalStyle';
import { SELECTION_TYPE } from '@/shared/constants/commonConstants';
import React, { useMemo, useState, useRef } from 'react';
import './_dynamicColumnTable.scss';
import {noop} from '@/shared/utils/noop';

/**
 * DynamicColumnTable component renders a PepsicoDsDataTable with dynamic columns and properties.
 *
 * @param {Object} props - The properties object.
 * @param {Object[]} props.columns - Array of column definitions.
 * @param {Object[]} props.records - Row data to be rendered in the table.
 * @param {string} [props.pklass=''] - Optional CSS class for the table container.
 * @param {boolean} [props.filters=true] - Flag to enable or disable filters.
 * @param {Object} [props.rowSelection={}] - Selected row(s) initial state.
 * @param {Function} [props.setRowSelection=() => {}] - Function to update the row selection state.
 * @param {Function} [props.onUpdate=() => {}] - Callback function to handle updates.
 * @param {boolean} [props.commonCellDataCheck=false] - Flag to check for common cell data.
 * @param {boolean} [props.isDark=true] - The mode of the table.
 * @param {string} [props.tableHeader=''] - The table heading.
 * @param {string} [props.size='small'] - The size of the table. Options: "small" | "medium" | "large".
 * @param {string} [props.selection='multiple'] - The selection mode of the table.
 * @param {number} [props.pageSize=500] - The page size of the table.
 * @param {number} [props.pageIndex=0] - The page index of the table.
 * @param {string} [props.tableDescription=''] - The table description.
 * @param {Object[]} [props.toolbarActions=[]] - The toolbar actions for the table.
 * @param {string} [props.searchPlaceholder='Search'] - Helper text for the search input.
 * @param {boolean} [props.showFilterButton=false] - Show filter button to display filter toolbar.
 * @param {boolean} [props.showFilterToolbar=false] - Prop to show filter toolbar with initial rendering.
 * @param {Function} [props.setSelectedRowData=() => {}] - Callback to set selected row data.
 * @param {boolean} [props.isExpandable=false] - Prop to enable expandable rows.
 * @param {Function} [props.renderSubComponent=() => {}] - Function to render the sub component.
 * @param {boolean} [props.showColumnFilters=true] - Prop to show column filters.
 * @param {boolean} [props.enableSorting=true] - Prop to enable sorting.
 * @param {Function} [props.onGlobalSearchChange=() => {}] - Function to handle global search input change.
 * @param {Function} [props.onSortChange=() => {}] - Function to handle column sort change.
 * @param {Function} [props.onColumnSearchChange=() => {}] - Function to handle column search input change.
 * @param {boolean} [props.showGlobalActions=true] - Show global actions bar with search, pagination and filtermenu.
 * @param {Function} [props.setPageIndex=() => {}] - Callback for page index changes.
 * @param {Function} [props.setSearchArry=() => {}] - Callback for search array changes.
 * @param {Function} [props.setSortingObj=() => {}] - Callback for sorting changes.
 * @param {boolean} [props.isLoading=false] - Loading state of the table.
 * @param {Function} [props.setIsSelectAll=() => {}] - Callback function to update row selection state.
 * @param {boolean} [props.manualPagination=true] - Enable manual pagination control.
 * @param {number} [props.totalRowsCount=400] - Total count of rows for manual pagination.
 * @param {string} [props.id=''] - The id of the table.
 * @returns {JSX.Element} The rendered DynamicColumnTable component.
 */

export const DynamicColumnTable = ({
  columns,
  records,
  pklass = '',
  filters = true,
  rowSelection = {},
  setRowSelection = noop,
  onUpdate = noop,
  commonCellDataCheck = false,
  isDark = true,
  tableHeader = '',
  size = SIZE.SMALL,
  selection = SELECTION_TYPE.MULTIPLE,
  pageSize = 500,
  pageIndex = 0,
  tableDescription = '',
  toolbarActions = [],
  searchPlaceholder = 'Search',
  showFilterButton = false,
  showFilterToolbar = false,
  showColumnFilters = true,
  enableSorting = true,
  showGlobalActions = true,
  showPagination = true,
  showSearchOption = true,
  showTableHeader = true,
  showTableSettings = true,
  rowActions = [],
  setPageIndex = noop,
  setSearchArry = noop,
  setSortingObj = noop,
  isLoading = false,
  setIsSelectAll = noop,
  manualPagination = true,
  totalRowsCount = 400,
  id = '',
  isExpandable = false,
  renderSubComponent = noop,
  onSortChange = noop,
}) => {
  const tableRef = useRef(null);
  const [prevRowSelectedLength, setPrevRowSelectedLength] = useState(0);

  const orderedColumns = useMemo(
    () => sortColumns(commonCellDataCheck ? getCellDataColumns(columns) : columns),
    [columns, commonCellDataCheck]
  );

  const { columns: orderedTableColumns, filters: orderedFilters } = useMemo(
    () =>
      columns && records
        ? getFilteredColumnsAndData(orderedColumns, records, !!filters)
        : { columns: [], filters: [] },
    [orderedColumns, records, filters, columns]
  );

  const getType = (type) => {
    return type === 'text' ? 'string' : type;
  };

  console.log("global", showGlobalActions)
  const handleRowSelection = (selectedRow) => {
    setRowSelection(selectedRow);
    const rowSelectionLength = Object.keys(selectedRow).length;
    if (prevRowSelectedLength + 1 < rowSelectionLength) {
      setIsSelectAll(true);
    } else if (prevRowSelectedLength < rowSelectionLength) {
      setIsSelectAll(false);
    }
    setPrevRowSelectedLength(rowSelectionLength);
  };

  const handleSorting = (sortingState) => {
    if (sortingState?.length) {
      const { id, desc } = sortingState[0];
      onSortChange(id, desc ? 'descending' : 'ascending');
    } else {
      onSortChange('', 'clear');
    }
    setSortingObj(sortingState?.length ? sortingState[0] : null);
  };

  if (!columns || !records) {
    return <p className="text-red-500">Columns and records are required to render the table.</p>;
  }

  return (
    <div className={`dynamic-table-container ${pklass ?? ''} dynamic-column-table`} ref={tableRef}>
      {isLoading || orderedTableColumns?.length == 0 ? (
        <TableSkeletonLoader rows={6} spacing={30} />
      ) : (
        <PepsicoDsDataTable
          id={`data_table` + (id ? `_${id}` : '')}
          data-testid={`data_table` + (id ? `_${id}` : '')}
          columns={orderedTableColumns}
          data={records}
          isDark={isDark}
          manualPagination={manualPagination}
          totalRowsCount={totalRowsCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          tableHeader={tableHeader}
          tableDescription={tableDescription}
          selection={selection}
          size={size}
          filters={filters ? orderedFilters : []}
          onUpdate={onUpdate}
          toolbarActions={toolbarActions}
          rowSelection={rowSelection}
          searchPlaceholder={searchPlaceholder}
          showFilterButton={showFilterButton}
          showFilterToolbar={showFilterToolbar}
          isExpandable={isExpandable}
          renderSubComponent={renderSubComponent}
          showColumnFilters={showColumnFilters}
          enableSorting={enableSorting}
          showPagination={showPagination}
          showSearchOption={showSearchOption}
          showTableHeader={showTableHeader}
          showTableSettings={showTableSettings}
          rowActions={rowActions}
          showGlobalActions={showGlobalActions}
          rowSelectionStateUpdater={handleRowSelection}
          onPageIndexChange={(index) => setPageIndex(index - 1)}
          onColumnFiltersChange={(value, type, name) => {
            setSearchArry({ field: name, value, type: getType(type) });
          }}
          sortingHandler={handleSorting}
        />
      )}
    </div>
  );
};

DynamicColumnTable.propTypes = {
  columns: PropTypes.array.isRequired,
  records: PropTypes.array.isRequired,
  pklass: PropTypes.string,
  filters: PropTypes.bool,
  rowSelection: PropTypes.object,
  setRowSelection: PropTypes.func,
  onUpdate: PropTypes.func,
  commonCellDataCheck: PropTypes.bool,
  isDark: PropTypes.bool,
  tableHeader: PropTypes.string,
  size: PropTypes.string,
  selection: PropTypes.string,
  pageSize: PropTypes.number,
  pageIndex: PropTypes.number,
  tableDescription: PropTypes.string,
  toolbarActions: PropTypes.array,
  searchPlaceholder: PropTypes.string,
  showFilterButton: PropTypes.bool,
  showFilterToolbar: PropTypes.bool,
  showColumnFilters: PropTypes.bool,
  enableSorting: PropTypes.bool,
  showGlobalActions: PropTypes.bool,
  showPagination: PropTypes.bool,
  showSearchOption: PropTypes.bool,
  showTableHeader: PropTypes.bool,
  showTableSettings: PropTypes.bool,
  rowActions: PropTypes.array,
  setPageIndex: PropTypes.func,
  setSearchArry: PropTypes.func,
  setSortingObj: PropTypes.func,
  isLoading: PropTypes.bool,
  setIsSelectAll: PropTypes.func,
  manualPagination: PropTypes.bool,
  totalRowsCount: PropTypes.number,
  id: PropTypes.string,
  isExpandable: PropTypes.bool,
  renderSubComponent: PropTypes.func,
  onSortChange: PropTypes.func
};

// Helper Functions
function sortColumns(columns) {
  return columns?.sort((a, b) => (a.order_of_column || 0) - (b.order_of_column || 0));
}

function createColumnData(column) {
  return {
    columnKey: column.name_of_db_column,
    header: column.header || column.label_of_column,
    initialSize: column.initialSize,
    minSize: column.minSize,
    maxSize: column.maxSize,
    cellData: column.cellData,
    filterVariant: column.filterVariant,
    columnFilterPlaceholder: column.label_of_column,
  };
}

function generateFilterOptions(columns, records) {
  const filterableColumns = columns.filter((column) => column.filterable);
  const optionsByColumn = filterableColumns.reduce((acc, column) => {
    const columnName = column.name_of_db_column;

    if (!acc[columnName]) {
      acc[columnName] = new Map();
    }

    records.forEach((record) => {
      const value = record[columnName];
      acc[columnName].set(value, {
        id: value,
        label: value,
      });
    });

    return acc;
  }, {});
  return optionsByColumn;
}

function createFilterItem(column, filterOptions) {
  const options = Array.from((filterOptions[column.name_of_db_column] || new Map()).values());
  return {
    disabled: false,
    label: column.label_of_column,
    listLabel: column.label_of_column,
    options,
    isDark: true,
    showSearch: true,
    selection: 'multiple',
    onChange: () => {},
  };
}

function getFilteredColumnsAndData(columns, records, isFilterEnabled) {
  const filterOptions = generateFilterOptions(columns, records);

  return columns.reduce(
    (acc, column) => {
      acc.columns.push(createColumnData(column));

      if (isFilterEnabled && column.filterable) {
        acc.filters.push(createFilterItem(column, filterOptions));
      }

      return acc;
    },
    { columns: [], filters: [] }
  );
}